<script setup lang="ts">
import { features, useAccessControlStore, useAuthStore, useFeatureFlag } from '@peanuds/app-shell';
import { toBrowserDateOnly } from '@peanuds/shared';
import {
  PAYMENTS_APPROVAL_FETCH_OPTIONS,
  useAccountSelection,
  usePaymentsApprovalCountStore,
} from '@peanuds/shared-state';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import AppNav from './AppNav.vue';

const authStore = useAuthStore();
const { logout } = authStore;

const accessControlStore = useAccessControlStore();
const { isOnboarded } = storeToRefs(accessControlStore);
const isPaymentsApprovalEnabled = useFeatureFlag(features.paymentsApproval);

const { resolvedTransactionAccountIds, resolvedTransactionAccountIdsKey } = useAccountSelection();

const paymentsApprovalCountStore = usePaymentsApprovalCountStore();
const { paymentsApprovalCount, paymentsApprovalCountError } = storeToRefs(
  paymentsApprovalCountStore,
);
const { fetchPaymentsApprovalCountForAccounts, resetPaymentsApprovalCount } =
  paymentsApprovalCountStore;

const hasPaymentsApprovalCountError = computed(() => {
  return Boolean(paymentsApprovalCountError.value);
});

const getPaymentsApprovalCountFetchOptions = () => {
  return {
    ...PAYMENTS_APPROVAL_FETCH_OPTIONS,
    transferDateTo: toBrowserDateOnly(),
  };
};

const loadPaymentsApprovalCount = async (accountIds: string[]) => {
  resetPaymentsApprovalCount();
  await fetchPaymentsApprovalCountForAccounts(accountIds, getPaymentsApprovalCountFetchOptions());
};

watch(
  () => [isOnboarded.value, resolvedTransactionAccountIdsKey.value] as const,
  async ([onboarded, accountIdsKey], previousValue: readonly [boolean, string] | undefined) => {
    const [previousOnboarded, previousAccountIdsKey] = previousValue ?? [false, ''];

    if (!isPaymentsApprovalEnabled.value || !onboarded || accountIdsKey.length === 0) {
      resetPaymentsApprovalCount();
      return;
    }

    if (onboarded === previousOnboarded && accountIdsKey === previousAccountIdsKey) return;

    await loadPaymentsApprovalCount(resolvedTransactionAccountIds.value);
  },
  { immediate: true },
);
</script>

<template>
  <header class="app-header">
    <AppNav
      :on-logout="logout"
      :payments-approval-count="isPaymentsApprovalEnabled ? paymentsApprovalCount : 0"
      :payments-approval-has-error="
        isPaymentsApprovalEnabled ? hasPaymentsApprovalCountError : false
      "
    />
  </header>
</template>

<style lang="scss">
.app-header {
  margin: 0.75rem 1.5rem 0;
}
</style>
